/* eslint-disable tailwindcss/no-custom-classname */
/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';
import classNames from 'classnames';
import { PremiumProduct, PremiumStatus } from '../../types/car-insurance';
import { CmsProduct } from '../../types/cmsProduct';
import { CarInsurance } from '../../types/cmsTypes';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { formatNumberWithSeparator } from '../../utils/helpers';
import Button from '../Button';
import { CarInsuranceCtaBuilderInstance } from '../CarInsurance/CarInsuranceCtaBuilder';
import { useSafeCarInsuranceContext } from '../CarInsurance/context/useSafeCarInsurance';
import { isChatForQuoteCarrier } from '../CarInsurance/helpers';
import { useCarInsuranceCentralModal } from '../CarInsurance/WhatsappStickyModule';
import { WhatsappIcon } from '../icons/whatsapIcon';
import Spinner from '../Spinner';
import { Tag } from '../Tag';
import Tooltip from '../Tooltip';
import { StaticText } from './i18n';
import { CarInsuranceQuote } from './SectionPremiumCI';
import { getCiPriceValue } from './utils';

export type SectionCarInsuranceProps = {
  productWithLivePrice?: PremiumProduct;
  statusCarInsuranceRequest?: PremiumStatus;
  sendAnalyticsForButtonApply: () => void;
  ctaLinkOverride: string | undefined;
  carInsuranceSection?: CarInsurance;
  carInsuranceQuote?: CarInsuranceQuote;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  staticText: StaticText;
  countryCode: string;
  defaultStaticCiTooltip: string | undefined;
  defaultTemplateDynamicCiTooltip: string | undefined;
  product: CmsProduct;
  CarInsuranceCtaComponent: CarInsuranceCtaBuilderInstance | null;
};

/**
 * INS-6583: the base ("Starting at") price is not a quote for whatever the
 * customer picked in the search bar - it is derived from one fixed reference
 * driver/vehicle profile. These values fill any {placeholder} left in
 * CMS-authored tooltip copy, so the tooltip can never claim cover for a make
 * the carrier does not underwrite (customers used to only find that out on the
 * carrier's own site).
 */
const BASE_PRICE_REFERENCE_PROFILE: Record<string, string> = {
  age: '35',
  maritalStatus: 'married',
  gender: 'male',
  manufactureYear: '2021',
  make: 'Honda',
  model: 'Vezel 1.5',
  trim: '',
  ncd: '50%',
  experience: '10',
  claims: '0',
};

export const SectionCarInsurance: React.FC<SectionCarInsuranceProps> = ({
  productWithLivePrice,
  product,
  sendAnalyticsForButtonApply,
  staticText,
  carInsuranceSection,
  carInsuranceQuote,
  statusCarInsuranceRequest,
  defaultStaticCiTooltip,
  defaultTemplateDynamicCiTooltip,
  CarInsuranceCtaComponent,
}) => {
  const { brand } = useCmsClientContext();
  const { openModal } = useCarInsuranceCentralModal('CAR_INSURANCE');

  const carInsuranceContext = useSafeCarInsuranceContext();
  if (!carInsuranceContext) {
    return null;
  }

  const priceValue = getCiPriceValue(
    productWithLivePrice,
    carInsuranceQuote,
    carInsuranceSection,
  );

  const viewPrice = formatNumberWithSeparator({
    value: priceValue,
    thousandsSeparator: ',',
    alwaysTwoDecimals: true,
  });

  const basePriceTooltipTemplate =
    carInsuranceSection?.tooltipTemplateDynamic ||
    defaultTemplateDynamicCiTooltip ||
    staticText.static_pricing_default_tooltip;

  const basePriceTooltip = basePriceTooltipTemplate?.replace(
    /{(make|model|ncd|experience|claims|age|gender|maritalStatus|manufactureYear|trim)}/g,
    (_, key) => BASE_PRICE_REFERENCE_PROFILE[key] ?? '',
  );

  const staticTooltip =
    carInsuranceSection?.tooltipStatic ||
    defaultStaticCiTooltip ||
    staticText.live_pricing_default_tooltip;

  const showPriceCheck =
    brand === 'singsaver'
      ? statusCarInsuranceRequest === 'fail' ||
        statusCarInsuranceRequest === 'Incomplete' ||
        !productWithLivePrice
      : !carInsuranceQuote?.totalPremium;
  const hasPremium =
    brand === 'singsaver'
      ? productWithLivePrice?.discountValue ||
        productWithLivePrice?.originalPremium ||
        productWithLivePrice?.duePremium
      : carInsuranceQuote?.totalPremium;
  const isLivePricing = statusCarInsuranceRequest !== 'fail' && hasPremium;

  // The premiums request has finished and came back without a price. Products
  // that cannot be bought without one fall back to "Chat for Quote", which
  // hands the user to the existing WhatsApp submission flow (`CentralModalCI`).
  const isNoQuote = statusCarInsuranceRequest !== 'Incomplete' && !hasPremium;

  // INS-6577: GE returns no live price for models it does not underwrite (e.g.
  // Porsche), and its click-out only gets the customer rejected on the carrier
  // site, so the broker produces the quote manually instead.
  const showChatForQuote = isNoQuote && isChatForQuoteCarrier(product.title);

  const tooltipText = isLivePricing ? staticTooltip : basePriceTooltip;

  return (
    <div className="flex flex-wrap items-center justify-between gap-spacing/gap/soft-8px md:flex-col md:flex-nowrap md:items-end">
      {statusCarInsuranceRequest === 'Incomplete' && (
        <div className="flex justify-end gap-spacing/gap/soft-8px">
          <Spinner className="inline size-6" />
          <span>{staticText.fetch_price}...</span>
        </div>
      )}

      {statusCarInsuranceRequest !== 'Incomplete' && !showChatForQuote && (
        <div className="flex items-start justify-end gap-spacing/gap/soft-8px md:flex-col lg:flex-row 2sm:items-start">
          {isLivePricing && (
            <div className="order-2 flex justify-end md:order-1">
              <Tag size="regular" color="orange" icon="bolt">
                {staticText.live_pricing}
              </Tag>
            </div>
          )}
          {viewPrice !== '0' && (
            <div
              className={classNames(
                'order-1 flex items-center text-right md:order-2',
                { 'gap-spacing/gap/soft-6px': !carInsuranceSection?.prefix },
              )}
            >
              <span className="mhg-alpha-body-2 text-text/secondary-lighter">
                {staticText.starting_at}
              </span>
              <span className="mhg-alpha-title-1 relative">
                {carInsuranceSection?.prefix && (
                  <>
                    <span className="opacity-0">
                      {carInsuranceSection?.prefix}{' '}
                    </span>
                    <span className="absolute -top-1 left-2 scale-75 text-text/secondary-lighter">
                      {carInsuranceSection?.prefix}
                    </span>
                  </>
                )}

                {viewPrice}
                {carInsuranceSection?.suffix}
              </span>
            </div>
          )}
        </div>
      )}
      <div className="flex w-full justify-end gap-2 md:w-auto">
        {showChatForQuote ? (
          <div className="w-full min-w-52 max-w-full md:max-w-52 lg:min-w-67 lg:max-w-67">
            <Button
              data-component="car-insurance-chat-for-quote-cta"
              styleType="primary"
              onClick={openModal}
              className="w-full"
              endIcon={<WhatsappIcon color="white" size={18} />}
            >
              {staticText.chat_for_quote}
            </Button>
          </div>
        ) : (
          <>
            {brand !== 'moneymax' && (
              <div className="flex size-[30px] shrink-0 items-center justify-center gap-2 rounded-xl bg-[#25D366] text-white md:size-10 lg:size-11">
                <button type="button" onClick={openModal}>
                  <WhatsappIcon
                    color="white"
                    size={18}
                    className="size-[18px] md:size-6"
                  ></WhatsappIcon>
                </button>
              </div>
            )}
            <div className="w-full min-w-52 max-w-full md:max-w-52 lg:min-w-67 lg:max-w-67">
              {CarInsuranceCtaComponent && (
                <CarInsuranceCtaComponent
                  className="w-full"
                  sendAnalytics={sendAnalyticsForButtonApply}
                  product={product}
                  staticText={staticText}
                />
              )}
            </div>
          </>
        )}
      </div>
      {!showChatForQuote && (
        <div className="mhg-alpha-caption flex items-center justify-end text-text/secondary-lighter">
          <span className="mr-1 text-right font-font-weight/table-cell-normal text-font-size/table-cell">
            {showPriceCheck ? (
              <span
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: staticText.approx_indicative_price,
                }}
              ></span>
            ) : (
              staticText.approx_price
            )}
          </span>
          {tooltipText && (
            <Tooltip content={tooltipText} popup-style="max-w-[300px]" />
          )}
        </div>
      )}
    </div>
  );
};
